/**
 * /dashboards — 대시보드 목록 페이지.
 *
 * 사이드바의 "대시보드" 메뉴 클릭 시 진입. 탭으로 "내 대시보드" / "공유받은 대시보드" 전환.
 * 사이드바에 N개 행을 다 펼치지 않고 단일 메뉴 → 별도 페이지 라는 사용자 결정에 맞춤.
 */
import { SidebarOpenerButton } from '@/components/layout/SidebarOpenerButton';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import type {
  DashboardSummary,
  DashboardVisibility,
  SharedDashboardSummary,
} from '@innermate/shared';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Globe, LayoutDashboard, Lock, Users } from 'lucide-react';

type DashboardTab = 'mine' | 'shared';

export function DashboardsIndex() {
  const navigate = useNavigate();
  const search = useSearch({ from: '/dashboards/' }) as { tab: DashboardTab };
  const tab = search.tab;

  const mineQuery = useQuery({
    queryKey: ['dashboards', 'mine'],
    queryFn: api.dashboardListMine,
    staleTime: 10_000,
  });
  const sharedQuery = useQuery({
    queryKey: ['dashboards', 'shared'],
    queryFn: api.dashboardListShared,
    staleTime: 10_000,
  });

  const mine = mineQuery.data?.dashboards ?? [];
  const shared = sharedQuery.data?.dashboards ?? [];

  const setTab = (t: DashboardTab) => {
    navigate({ to: '/dashboards', search: { tab: t }, replace: true });
  };

  return (
    <div className="flex flex-col h-full">
      <header className="flex items-center gap-2 px-6 py-3 border-b border-border bg-background sticky top-0 z-10">
        <SidebarOpenerButton />
        <h1 className="text-base font-semibold">대시보드</h1>
        <div className="flex-1" />
        <p className="text-xs text-foreground-muted hidden sm:block">
          새 대시보드는 채팅에서 차트를 만들고 "저장" 으로 추가
        </p>
      </header>

      <div className="px-6 pt-4">
        <div
          role="tablist"
          aria-label="대시보드 분류"
          className="inline-flex gap-1 rounded-lg bg-surface-muted/60 p-1"
        >
          <TabButton
            active={tab === 'mine'}
            onClick={() => setTab('mine')}
            label="내 대시보드"
            count={mine.length}
          />
          <TabButton
            active={tab === 'shared'}
            onClick={() => setTab('shared')}
            label="공유받은 대시보드"
            count={shared.length}
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {tab === 'mine' ? (
          <MineTab dashboards={mine} loading={mineQuery.isLoading} />
        ) : (
          <SharedTab dashboards={shared} loading={sharedQuery.isLoading} />
        )}
      </div>
    </div>
  );
}

interface TabButtonProps {
  active: boolean;
  onClick: () => void;
  label: string;
  count: number;
}

function TabButton({ active, onClick, label, count }: TabButtonProps) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm transition-colors',
        active
          ? 'bg-background text-foreground shadow-sm'
          : 'text-foreground-muted hover:text-foreground',
      )}
    >
      <span>{label}</span>
      <span
        className={cn(
          'inline-flex items-center justify-center rounded-full px-1.5 min-w-[1.25rem] h-5 text-xs',
          active ? 'bg-accent text-accent-foreground' : 'bg-surface-muted text-foreground-muted',
        )}
      >
        {count}
      </span>
    </button>
  );
}

function MineTab({
  dashboards,
  loading,
}: {
  dashboards: DashboardSummary[];
  loading: boolean;
}) {
  if (loading) return <div className="p-6 text-sm text-foreground-muted">불러오는 중…</div>;
  if (dashboards.length === 0) {
    return <EmptyState text={'채팅에서 차트를 만들고 "저장" 하면 여기에 보입니다.'} />;
  }
  return (
    <div className="grid gap-4 p-6 grid-cols-[repeat(auto-fill,minmax(260px,1fr))]">
      {dashboards.map((d) => (
        <DashboardCard key={d.id} dashboard={d} />
      ))}
    </div>
  );
}

function SharedTab({
  dashboards,
  loading,
}: {
  dashboards: SharedDashboardSummary[];
  loading: boolean;
}) {
  if (loading) return <div className="p-6 text-sm text-foreground-muted">불러오는 중…</div>;
  if (dashboards.length === 0) {
    return <EmptyState text="아직 공유받은 대시보드가 없어요." />;
  }
  return (
    <div className="grid gap-4 p-6 grid-cols-[repeat(auto-fill,minmax(260px,1fr))]">
      {dashboards.map((d) => (
        <SharedDashboardCard key={d.id} dashboard={d} />
      ))}
    </div>
  );
}

function EmptyState({ text }: { text: string }) {
  return (
    <div className="h-full grid place-items-center px-6 py-16">
      <div className="max-w-md text-center">
        <LayoutDashboard className="size-12 mx-auto text-foreground-muted opacity-30" />
        <p className="mt-6 text-sm text-foreground-muted leading-relaxed">{text}</p>
      </div>
    </div>
  );
}

function visibilityIconFor(visibility: DashboardVisibility) {
  return visibility === 'public' ? Globe : visibility === 'group' ? Users : Lock;
}

function visibilityLabelFor(visibility: DashboardVisibility) {
  return visibility === 'public' ? '전체 공유' : visibility === 'group' ? '그룹 공유' : '비공개';
}

function DashboardCard({ dashboard }: { dashboard: DashboardSummary }) {
  const Icon = visibilityIconFor(dashboard.visibility);
  return (
    <Link
      to="/dashboard/view/$dashboardId"
      params={{ dashboardId: dashboard.id }}
      className="group flex flex-col gap-3 rounded-lg border border-border bg-surface-muted/30 hover:bg-surface-muted/60 hover:border-accent/30 transition-colors p-4 min-h-[140px]"
    >
      <div className="flex items-start gap-2.5">
        <span className="grid place-items-center size-9 rounded-md bg-accent/10 text-accent shrink-0">
          <LayoutDashboard className="size-4" />
        </span>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-medium truncate">{dashboard.title}</h3>
          <p className="text-xs text-foreground-muted mt-0.5 inline-flex items-center gap-1">
            <Icon className="size-3" />
            <span>{visibilityLabelFor(dashboard.visibility)}</span>
          </p>
        </div>
      </div>
      <div className="mt-auto text-xs text-foreground-muted">
        {formatRelative(dashboard.updatedAt)} 업데이트
      </div>
    </Link>
  );
}

function SharedDashboardCard({ dashboard }: { dashboard: SharedDashboardSummary }) {
  const Icon = visibilityIconFor(dashboard.visibility);
  const chipText =
    dashboard.visibility === 'group' && dashboard.groupName
      ? dashboard.groupName
      : visibilityLabelFor(dashboard.visibility);
  return (
    <Link
      to="/dashboard/view/$dashboardId"
      params={{ dashboardId: dashboard.id }}
      className="group flex flex-col gap-3 rounded-lg border border-border bg-surface-muted/30 hover:bg-surface-muted/60 hover:border-accent/30 transition-colors p-4 min-h-[140px]"
    >
      <div className="flex items-start gap-2.5">
        <span className="grid place-items-center size-9 rounded-md bg-accent/10 text-accent shrink-0">
          <LayoutDashboard className="size-4" />
        </span>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-medium truncate">{dashboard.title}</h3>
          <p className="text-xs text-foreground-muted mt-0.5 truncate">
            {dashboard.ownerDisplayName} 님이 공유
          </p>
        </div>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] bg-surface-muted text-foreground-muted">
          <Icon className="size-3" />
          {chipText}
        </span>
      </div>
      <div className="mt-auto text-xs text-foreground-muted">
        {formatRelative(dashboard.updatedAt)} 업데이트
      </div>
    </Link>
  );
}

function formatRelative(iso: string): string {
  const d = new Date(iso);
  const now = Date.now();
  const diffMs = now - d.getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return '방금';
  if (mins < 60) return `${mins}분 전`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}시간 전`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}일 전`;
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
}
